/**
 * GeoVera Studio payments — Razorpay Checkout (merchant: SIFA Geolabs)
 */
import { apiJson } from './apiClient';

export interface StudioPlan {
  code: string;
  name: string;
  amountPaise: number;
  amountInr: number;
  currency: string;
  periodDays: number;
}

export async function fetchPaymentConfig() {
  return apiJson<{
    success: boolean;
    enabled: boolean;
    keyId: string | null;
    merchant: string;
    product: string;
    plans: StudioPlan[];
  }>('/api/payments/config');
}

export async function createStudioOrder(body: {
  planCode?: string;
  name?: string;
  email: string;
  organization?: string;
}) {
  return apiJson<{
    success: boolean;
    keyId: string;
    order: { id: string; amount: number; currency: string; receipt?: string };
    plan: StudioPlan & { amountPaise: number };
    checkout: {
      name: string;
      description: string;
      prefill: { name: string; email: string };
      theme: { color: string };
    };
  }>('/api/payments/studio/order', {
    method: 'POST',
    body,
    retry: false,
  });
}

export async function verifyStudioPayment(body: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}) {
  return apiJson<{
    success: boolean;
    message?: string;
    planCode?: string;
    activeUntil?: string;
    paymentId?: string;
  }>('/api/payments/studio/verify', {
    method: 'POST',
    body,
    retry: false,
  });
}

export async function fetchStudioStatus() {
  return apiJson<{
    success: boolean;
    merchant: string;
    product: string;
    entitlement: {
      plan: string | null;
      activeUntil: string | null;
      active: boolean;
    };
    payments: Array<{
      plan_code: string;
      amount_paise: number;
      currency: string;
      status: string;
      paid_at?: string;
      razorpay_payment_id?: string;
      merchant_label?: string;
      product_label?: string;
    }>;
  }>('/api/payments/studio/status');
}

export async function adminListPayments() {
  return apiJson<{
    success: boolean;
    payments: Array<Record<string, unknown>>;
    count: number;
  }>('/api/payments/admin/list');
}

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, cb: (resp: unknown) => void) => void;
    };
  }
}

let razorpayScriptPromise: Promise<void> | null = null;

export function loadRazorpayScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));
  if (window.Razorpay) return Promise.resolve();
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      razorpayScriptPromise = null;
      reject(new Error('Failed to load Razorpay Checkout'));
    };
    document.body.appendChild(s);
  });
  return razorpayScriptPromise;
}

/**
 * Full Studio checkout flow.
 */
export async function startStudioCheckout(opts: {
  planCode?: string;
  name: string;
  email: string;
  organization?: string;
}): Promise<{ paymentId: string; activeUntil?: string }> {
  await loadRazorpayScript();
  if (!window.Razorpay) throw new Error('Razorpay Checkout not available');

  const created = await createStudioOrder({
    planCode: opts.planCode || 'studio_monthly',
    name: opts.name,
    email: opts.email,
    organization: opts.organization,
  });

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay!({
      key: created.keyId,
      amount: created.order.amount,
      currency: created.order.currency,
      name: created.checkout.name, // SIFA Geolabs
      description: created.checkout.description, // GeoVera Studio
      order_id: created.order.id,
      prefill: created.checkout.prefill,
      theme: created.checkout.theme,
      handler: async (response: {
        razorpay_order_id: string;
        razorpay_payment_id: string;
        razorpay_signature: string;
      }) => {
        try {
          const verified = await verifyStudioPayment(response);
          if (!verified.success) {
            reject(new Error(verified.message || 'Verification failed'));
            return;
          }
          resolve({
            paymentId: response.razorpay_payment_id,
            activeUntil: verified.activeUntil,
          });
        } catch (e) {
          reject(e);
        }
      },
      modal: {
        ondismiss: () => reject(new Error('Payment cancelled')),
      },
    });
    rzp.on('payment.failed', (resp: unknown) => {
      reject(new Error('Payment failed'));
      console.warn('Razorpay payment.failed', resp);
    });
    rzp.open();
  });
}
